import { useEffect, useState } from 'react'
import { AlertTriangle, KeyRound } from 'lucide-react'
import { Drawer, DrawerField, DrawerSection } from '@/shared/ui/Drawer'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { fmtDateTime, fmtNumber, humanise } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import { apiKeyReport, getApiKey } from './api'
import { apiKeyReportModel } from './report'
import { RangePicker, ReportBody, rangeParams, type RangeId } from './ReportPanels'

/**
 * What a key is, and what it has done.
 *
 * Everything shown here was already stored on the row and simply unreachable —
 * the authorities the key carries, the geography it is pinned to, the addresses
 * it may call from. A key you cannot inspect is a key nobody can safely keep, so
 * the detail view is the difference between an issued credential and an audited
 * one.
 *
 * The one thing deliberately absent, here as everywhere in this slice, is the
 * key itself. It existed in exactly one response body, at creation.
 */
export function ApiKeyDrawer({ keyId, onClose }: { keyId: string | null; onClose: () => void }) {
  const [tab, setTab] = useState<'detail' | 'usage'>('detail')
  const [range, setRange] = useState<RangeId>('7d')

  // Opening a *different* key must start on Detail. Without this the drawer
  // keeps whichever tab was last used, so the second key you inspect silently
  // shows a usage chart instead of the identity you clicked in to read.
  useEffect(() => { setTab('detail') }, [keyId])

  const detail = useLoad(() => (keyId ? getApiKey(keyId) : Promise.resolve(null)), [keyId])
  const report = useLoad(
    () => (keyId && tab === 'usage' ? apiKeyReport(keyId, rangeParams(range)) : Promise.resolve(null)),
    [keyId, tab, range],
  )

  const k = detail.data
  const model = report.data ? apiKeyReportModel(report.data) : null

  // `lastUsedAt` / `useCount` are written by the api-key call interceptor. Zero
  // across the board more often means "no writer" than "never used", and saying
  // so is more useful than rendering a confident zero.
  const neverObserved = !!k && !k.lastUsedAt && k.useCount === 0

  return (
    <Drawer
      open={keyId !== null}
      onClose={onClose}
      title={k?.name ?? 'API key'}
      subtitle={k ? `${k.prefix}… · ${humanise(k.status)}` : undefined}
      width="lg"
    >
      <ErrorStrip message={detail.error} />

      <div className="flex items-center gap-0.5 border-b border-border px-4">
        {(['detail', 'usage'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              'px-3 py-2 text-erp-sm border-b-2 -mb-px capitalize',
              tab === t
                ? 'border-chocolate text-chocolate font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'detail' && k && (
        <>
          <DrawerSection title="Authorities">
            <p className="px-4 pt-2 text-erp-xs text-muted-foreground">
              What this key may do. It can never exceed the authorities of whoever issued it.
            </p>
            {k.authorities?.length ? (
              <ul className="flex flex-wrap gap-1.5 px-4 py-2">
                {[...k.authorities].sort().map((a) => (
                  <li
                    key={a}
                    className="px-1.5 py-0.5 rounded-md border border-border bg-muted/40 font-mono text-erp-xs"
                  >
                    {a}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-4 py-2 text-erp-sm text-muted-foreground">
                No authorities recorded on this key.
              </p>
            )}
          </DrawerSection>

          <DrawerSection title="Scope and limits">
            <DrawerField label="Scope level" value={humanise(k.scopeLevel)} />
            <DrawerField label="Scope unit" value={k.scopeUnitName ?? (k.scopeUnitId ? k.scopeUnitId : 'National — no unit')} />
            <DrawerField label="Rate limit" value={k.rateLimitPerMinute ? `${fmtNumber(k.rateLimitPerMinute)} / minute` : '—'} />
            <DrawerField
              label="IP allowlist"
              value={
                k.ipAllowlist?.length ? (
                  <span className="font-mono text-erp-xs">{k.ipAllowlist.join(', ')}</span>
                ) : (
                  'Any address'
                )
              }
            />
            <DrawerField label="Expires" value={fmtDateTime(k.expiresAt)} />
          </DrawerSection>

          <DrawerSection title="Activity">
            <DrawerField
              label="Last used"
              value={k.lastUsedAt ? fmtDateTime(k.lastUsedAt) : <span className="text-muted-foreground">Never observed</span>}
            />
            <DrawerField label="Last address" value={k.lastUsedIp ? <span className="font-mono text-erp-xs">{k.lastUsedIp}</span> : '—'} />
            <DrawerField label="Calls recorded" value={fmtNumber(k.useCount)} />
            {neverObserved && (
              <div className="p-3">
                <Note tone="info">
                  <span className="inline-flex items-start gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>
                      Nothing has been recorded against this key. That is either a key nobody has
                      used, or a backend that is not yet recording per-request usage — check the
                      Usage tab before treating it as idle.
                    </span>
                  </span>
                </Note>
              </div>
            )}
          </DrawerSection>

          {k.status === 'REVOKED' && (
            <DrawerSection title="Revocation">
              <DrawerField label="Revoked" value={fmtDateTime(k.revokedAt)} />
              <DrawerField label="Reason" value={k.revokedReason ?? '—'} />
            </DrawerSection>
          )}
        </>
      )}

      {tab === 'usage' && (
        <div className="space-y-3 p-4">
          <div className="flex items-center justify-between gap-2">
            <span className="text-erp-sm text-muted-foreground inline-flex items-center gap-1.5">
              <KeyRound className="h-3.5 w-3.5" /> Requests made with this key
            </span>
            <RangePicker value={range} onChange={setRange} onRefresh={() => void report.refresh()} busy={report.loading} />
          </div>
          <ReportBody
            model={model}
            error={report.error}
            loading={report.loading}
            emptyHint="No requests were made with this key in this window. Routes are recorded as templates, so a key used once against many voters shows one row, not thousands."
          />
        </div>
      )}
    </Drawer>
  )
}

/** Small helper so the keys table can flag a key that is about to lapse. */
export function expiryTone(expiresAt: string): string | undefined {
  const days = (new Date(expiresAt).getTime() - Date.now()) / 86_400_000
  if (Number.isNaN(days)) return undefined
  if (days < 0) return 'text-red-700 dark:text-red-400'
  if (days < 14) return cn('text-amber-700 dark:text-amber-400')
  return undefined
}
